import debounce from 'lodash/debounce';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isBoolean from 'lodash/isBoolean';
import isEqual from 'lodash/isEqual';
import reduce from 'lodash/reduce';
import React, { Component } from 'react';

import Data from '../data';
import Grid from '../grid';
import Utils from '../utils';
import MediaMailSelectionModal from './MediaMailSelectionModal.component';

const { ServiceType } = Data;
const { GridPanel } = Grid;
const { withLayoutAndModalContexts } = Utils;

class CarrierServicesFilter extends Component {
	constructor(props) {
		super(props);

		const { rateAdvisorData = {} } = props;
		const { showMediaMailSelectionModal } = rateAdvisorData;

		this.state = {
			isShowMediaMailSelectionModal: showMediaMailSelectionModal === true,
			autoGroupColumnDef: {
				field: 'serviceName',
				cellRenderer: 'agGroupCellRenderer',
				suppressSizeToFit: true,
				valueFormatter: data => data.value,
				cellRendererParams: {
					suppressCount: true,
					checkbox: true,
					innerRenderer: data => {
						// remove any icons, spaces, and shifts to lower case
						const dataqa = data.value
							.replace(/(<([^>]+)>)/gi, '')
							.replace(/[^a-zA-Z]/g, '')
							.toLowerCase();

						return `<span data-qa=ui-checkedbox-options-carrier-${dataqa} {...{ dataqa: ui-checkedbox-options-carrier-${dataqa} }}>${data.value}</span>`;
					},
				},
			},
			columnDefs: [
				{
					field: 'carrier',
					filter: true,
					rowGroup: true,
					hide: true,
				},
			],
			rowData: [],
		};

		this.gridApi = null;
		this.onSelectionChanged = debounce(this.onSelectionChanged, 500);
	}

	componentDidUpdate(prevProps) {
		const { serviceResults } = this.props;

		if (!isEqual(prevProps.serviceResults, serviceResults)) {
			this.updateView(serviceResults);
		}
	}

	// notify the parent about the filter changes
	notifyParent = () => {
		const { onFiltersChange, rateAdvisorData } = this.props;
		const { rowData } = this.state;
		const services = rowData.reduce((result, service) => {
			result[service.serviceType] = service.checked;
			return result;
		}, {});

		/**
		 * Dev note:
		 * When the component loads and the data is loaded, we need to programmatically
		 * select the checkboxes per user's preference. The issue with this is that
		 * calling setSelected on rowNode api will trigger a number of selectionChanged
		 * events, which results in this method getting called multiple times.
		 *
		 * Solution:
		 * We are grouping a number of selectionChanged events into one by introducing
		 * debounce on onSelectionChanged handler. Another thing we can do also is
		 * check when to call onFiltersChange method.
		 */
		if (onFiltersChange && !isEqual(services, rateAdvisorData.services)) {
			onFiltersChange('services', services);
		}
	};

	updateView = serviceResults => {
		const { rateAdvisorData = {} } = this.props;
		const { services = {} } = rateAdvisorData;

		this.setState(
			{
				rowData: reduce(
					serviceResults,
					(_rowData, service) => {
						if (!find(_rowData, { serviceType: service.serviceType })) {
							_rowData.push({
								// per WEBAPPS-26886, system unchecks Media Mail by default
								checked: !isBoolean(services[service.serviceType])
									? service.serviceType !== ServiceType.UspsMediaMail
									: services[service.serviceType],
								carrier: service.carrierDisplayName,
								serviceType: service.serviceType,
								serviceName: service.serviceName,
							});
						}

						return _rowData;
					},
					[]
				),
			},
			this.updateServicesSelection
		);
	};

	updateServicesSelection = () => {
		if (this.gridApi) {
			/**
			 * Dev note:
			 * Calling setSelected will trigger 'onSelectionChange' event, which
			 * it could result in multiple network calls to save preferences.
			 * Use debounce to reduce the events.
			 */

			// automatically selects all rows nodes per user's preferences
			this.gridApi.forEachLeafNode(node => {
				node.setSelected(node.data.checked === true);
			});
		}
	};

	getRowNodeId = ({ serviceType }) => serviceType;

	getRowHeight = ({ node }) => (node.group ? 36 : 26);

	onGridReady = ({ api }) => {
		this.gridApi = api;

		// automatically select all rows nodes per user's preferences
		this.updateServicesSelection();
	};

	/**
	 * Handler after closing Media Mail modal. System continues with the selection flow
	 * prior to opening the modal.
	 * @param {boolean} dontShowCheckboxVal `true` when user clicks "Don't Warn Me Again" checkbox.
	 * @param {array} carrierServicesSelection Array of selected services prior to opening the modal.
	 */
	onMediaMailModalClose = (dontShowCheckboxVal, carrierServicesSelection) => {
		const isMediaMailSelected = includes(
			carrierServicesSelection,
			ServiceType.UspsMediaMail
		);

		this.setState(
			({ rowData }) => ({
				isShowMediaMailSelectionModal: !dontShowCheckboxVal,
				rowData: rowData.map(service => ({
					...service,
					checked: includes(carrierServicesSelection, service.serviceType),
				})),
			}),
			() => {
				this.updateServicesSelection();

				if (isMediaMailSelected === true) {
					this.notifyParent();
				}
			}
		);
	};

	// change the 'checked' property when selecting/deselecting an item in the grid
	onSelectionChanged = selection => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { isShowMediaMailSelectionModal, rowData } = this.state;

		// dev note:
		// `selection` array contains only selected services. If services
		// are not included in `selection` array, services are not selected.

		// build the lookup map
		const selectionLookup = selection.map(item => item.data.serviceType);

		// check if Media Mail is selected
		const currentMediaMailSelection = includes(
			selectionLookup,
			ServiceType.UspsMediaMail
		);

		// check if the last selected option is Media Mail
		const mediaMailSelection = rowData.find(
			service => service.serviceType === ServiceType.UspsMediaMail
		);
		const prevMediaMailSelection = mediaMailSelection
			? mediaMailSelection.checked === true
			: false;

		// when user selects Media Mail
		if (
			prevMediaMailSelection !== currentMediaMailSelection &&
			currentMediaMailSelection &&
			isShowMediaMailSelectionModal
		) {
			showModal({
				component: MediaMailSelectionModal,
				data: {
					carrierServicesSelection: selectionLookup,
					callback: this.onMediaMailModalClose,
				},
			});
		} else {
			this.setState(
				{
					rowData: rowData.map(service => ({
						...service,
						checked: includes(selectionLookup, service.serviceType),
					})),
				},
				() => {
					this.notifyParent();
				}
			);
		}
	};

	render() {
		const { columnDefs, rowData, autoGroupColumnDef } = this.state;

		return (
			<div className="common-rate-advisor-carrier-services-filter">
				<p className="carrier-title">Carriers & Services</p>
				{rowData.length > 0 && (
					<GridPanel
						rowData={rowData}
						columnDefs={columnDefs}
						showPagination={false}
						onGridReady={this.onGridReady}
						onSelectionChanged={this.onSelectionChanged}
						headerHeight={0}
						autoGroupColumnDef={autoGroupColumnDef}
						groupSelectsChildren
						groupSelectsFiltered
						rowMultiSelectWithClick
						deltaRowDataMode
						getRowNodeId={this.getRowNodeId}
						domLayout="autoHeight"
						getRowHeight={this.getRowHeight}
						dataqa="carrierservices-grid"
					/>
				)}
			</div>
		);
	}
}
export default withLayoutAndModalContexts(CarrierServicesFilter);
